import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { 
  CheckCircleIcon, 
  ShieldCheckIcon,
  TrophyIcon,
  ClockIcon 
} from '@heroicons/react/24/solid'

export default function Register() {
  const [searchParams] = useSearchParams()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [validationError, setValidationError] = useState('')
  const { register, loading, error } = useAuthStore()
  const navigate = useNavigate()

  const redirect = searchParams.get('redirect') || '/dashboard'

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (password !== passwordConfirm) {
      setValidationError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters')
      return
    }

    setValidationError('')
    const result = await register(name, email, password, passwordConfirm)
    if (result.success) {
      navigate(redirect)
    }
  }

  const displayError = validationError || error

  return (
    <div className="grid md:grid-cols-2 gap-12 items-center">
      {/* Left side - Benefits */}
      <div className="hidden md:block bg-gradient-to-br from-cte-blue to-cte-blue-dark rounded-2xl p-10 text-white">
        <div className="mb-8">
          <TrophyIcon className="h-12 w-12 mb-4" />
          <h3 className="text-3xl font-bold mb-3">Join 2,500+ Students</h3>
          <p className="text-blue-100">Master AI agent systems with our comprehensive course</p>
        </div>

        <div className="space-y-5 mb-8">
          <div className="flex items-start">
            <div className="bg-white/20 rounded-lg p-2 mr-3">
              <CheckCircleIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold mb-1">Complete Curriculum</div>
              <div className="text-blue-100 text-sm">10 modules from basics to advanced deployment</div>
            </div>
          </div>
          <div className="flex items-start">
            <div className="bg-white/20 rounded-lg p-2 mr-3">
              <TrophyIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold mb-1">Portfolio Projects</div>
              <div className="text-blue-100 text-sm">4 capstones to showcase your skills</div>
            </div>
          </div>
          <div className="flex items-start">
            <div className="bg-white/20 rounded-lg p-2 mr-3">
              <ClockIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold mb-1">Learn at Your Pace</div>
              <div className="text-blue-100 text-sm">Lifetime access with free updates</div>
            </div>
          </div>
          <div className="flex items-start">
            <div className="bg-white/20 rounded-lg p-2 mr-3">
              <ShieldCheckIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold mb-1">Risk-Free Guarantee</div>
              <div className="text-blue-100 text-sm">30-day money-back guarantee</div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Course Rating</span>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-300 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
          </div>
          <div className="text-2xl font-bold">4.9/5.0</div>
          <div className="text-sm text-blue-100">Based on 1,200+ reviews</div>
        </div>
      </div>

      {/* Right side - Registration Form */}
      <div>
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-cte-black mb-2">Create your account</h2>
          <p className="text-gray-600">Registration is free. Choose your tier after signing up.</p>
        </div>
        
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-700 text-sm font-medium">After registration, you'll select a pricing tier to access course content.</span>
          </div>
        </div>
        
        {displayError && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700 text-sm font-medium">{displayError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full name
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cte-blue focus:border-cte-blue transition-all"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cte-blue focus:border-cte-blue transition-all"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cte-blue focus:border-cte-blue transition-all"
              placeholder="Min. 8 characters"
              minLength={8}
            />
          </div>

          <div>
            <label htmlFor="password_confirm" className="block text-sm font-semibold text-gray-700 mb-2">
              Confirm password
            </label>
            <input
              id="password_confirm"
              type="password"
              required
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cte-blue focus:border-cte-blue transition-all"
              placeholder="Re-enter password"
              minLength={8}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-cte-blue text-white font-bold py-4 rounded-xl hover:bg-cte-blue-dark transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : (
              'Create free account'
            )}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                const apiUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000';
                window.location.href = `${apiUrl}/api/oauth/google`;
              }}
              className="w-full inline-flex justify-center items-center py-3 px-4 border-2 border-gray-200 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>

            <button
              onClick={() => {
                const apiUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000';
                window.location.href = `${apiUrl}/api/oauth/github`;
              }}
              className="w-full inline-flex justify-center items-center py-3 px-4 border-2 border-gray-200 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              GitHub
            </button>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-xs text-gray-500 text-center">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-cte-blue hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-cte-blue hover:underline">Privacy Policy</Link>
          </p>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-cte-blue hover:text-cte-blue-dark underline">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <Link 
            to="/" 
            className="text-sm text-gray-600 hover:text-cte-blue flex items-center justify-center"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}
